<template>
  <v-btn
      :loading="exporting"
      :disabled="disabled || exporting"
      color="success"
      elevation="2"
      @click="exportarExcel"
      v-bind="$attrs"
  >
    <v-icon class="mr-2">mdi-microsoft-excel</v-icon>
    <span>{{ label }}</span>
  </v-btn>
</template>

<script>
import * as XLSX from 'xlsx';

export default {
  name: 'ExcelExporter',
  props: {
    dados: {
      type: Array,
      required: true,
      default: () => []
    },
    filename: {
      type: String,
      default: 'exportacao'
    },
    label: {
      type: String,
      default: 'Exportar Excel'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    bonificacoes: {
      type: Array,
      default: () => []
    },
    tipo: {
      type: String,
      default: 'vendedores',
      validator: (value) => ['vendedores', 'convenios', 'motoristas'].includes(value)
    }
  },
  data: () => ({
    exporting: false
  }),
  methods: {
    async exportarExcel() {
      if (this.dados.length === 0) {
        this.$emit('error', 'Não há dados para exportar');
        return;
      }

      this.exporting = true;

      try {
        // Preparar os dados baseado no tipo
        const dadosExportacao = this.prepararDadosPorTipo();

        // Criar workbook
        const wb = XLSX.utils.book_new();

        // Criar worksheet
        const ws = XLSX.utils.aoa_to_sheet(dadosExportacao);

        // Aplicar estilos e largura das colunas
        this.aplicarEstilosPorTipo(ws, dadosExportacao);

        // Adicionar worksheet ao workbook
        const nomeAba = this.obterNomeAba();
        XLSX.utils.book_append_sheet(wb, ws, nomeAba);

        // Gerar arquivo
        const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '_');
        const nomeArquivo = `${this.filename}_${timestamp}.xlsx`;

        XLSX.writeFile(wb, nomeArquivo);

        this.$emit('success', 'Arquivo Excel exportado com sucesso!');
      } catch (error) {
        console.error('Erro ao exportar Excel:', error);
        this.$emit('error', 'Erro ao exportar arquivo Excel');
      } finally {
        this.exporting = false;
      }
    },

    obterNomeAba() {
      const nomes = {
        vendedores: 'Vendedores',
        convenios: 'Convênios',
        motoristas: 'Motoristas'
      };
      return nomes[this.tipo] || 'Dados';
    },

    prepararDadosPorTipo() {
      switch (this.tipo) {
        case 'vendedores':
          return this.prepararDadosVendedores();
        case 'convenios':
          return this.prepararDadosConvenios();
        case 'motoristas':
          return this.prepararDadosMotoristas();
        default:
          return this.prepararDadosVendedores();
      }
    },

    prepararDadosVendedores() {
      const dados = [];

      // Cabeçalho
      const cabecalho = [
        'Filial',
        'Vendedor',
        'Meta',
        'Venda Base',
        'Comissão Base'
      ];

      // Adicionar colunas de bonificações dinamicamente
      this.bonificacoes.forEach(bonif => {
        cabecalho.push(`Venda ${bonif}`);
        cabecalho.push(`Comissão ${bonif}`);
      });

      cabecalho.push('Salário Total');
      dados.push(cabecalho);

      // Dados dos vendedores
      this.dados.forEach(vendedor => {
        const linha = [
          vendedor.idfilial,
          vendedor.vendedor,
          vendedor.meta || 0,
          vendedor.vendabase || 0,
          vendedor.comissaobase || 0
        ];

        // Adicionar dados de bonificações
        this.bonificacoes.forEach(bonif => {
          const vendaBonif = vendedor.bonificacoes?.[bonif]?.vendatotal || 0;
          const comissaoBonif = vendedor.bonificacoes?.[bonif]?.comissao || 0;

          linha.push(vendaBonif);
          linha.push(comissaoBonif);
        });

        linha.push(vendedor.salario || 0);
        dados.push(linha);
      });

      // Linha de totais
      const totais = ['TOTAL GERAL', ''];
      totais.push(this.somarCampo('meta'));
      totais.push(this.somarCampo('vendabase'));
      totais.push(this.somarCampo('comissaobase'));

      this.bonificacoes.forEach(bonif => {
        totais.push(this.somarBonificacaoVenda(bonif));
        totais.push(this.somarBonificacaoComissao(bonif));
      });

      totais.push(this.somarCampo('salario'));
      dados.push(totais);

      return dados;
    },

    prepararDadosConvenios() {
      const dados = [];

      // Cabeçalho
      dados.push([
        'Filial',
        'Vendedor',
        'Total Notas',
        'Comissão'
      ]);

      // Dados
      this.dados.forEach(convenio => {
        dados.push([
          convenio.idfilial,
          convenio.vendedor,
          convenio.qtd_notas || 0,
          convenio.salario || 0
        ]);
      });

      // Totais
      dados.push([
        'TOTAL GERAL',
        '',
        this.somarCampo('qtd_notas'),
        this.somarCampo('salario')
      ]);

      return dados;
    },

    prepararDadosMotoristas() {
      const dados = [];

      // Cabeçalho
      dados.push([
        'Filial',
        'Motorista',
        'Meta Entregas',
        'Qtd Entregas',
        'Valor/Entrega',
        'Bonificação',
        'Status Meta'
      ]);

      // Dados
      this.dados.forEach(motorista => {
        const metaEntregas = motorista.meta_entregas || 0;
        const qtdEntregas = motorista.qtd_entregas || 0;
        const statusMeta = qtdEntregas >= metaEntregas ? 'ATINGIU' : 'NÃO ATINGIU';

        dados.push([
          motorista.idfilial,
          motorista.vendedor,
          metaEntregas,
          qtdEntregas,
          motorista.valor_por_entrega || 0,
          motorista.valor_bonificacao || 0,
          statusMeta
        ]);
      });

      // Totais
      dados.push([
        'TOTAL GERAL',
        '',
        this.somarCampo('meta_entregas'),
        this.somarCampo('qtd_entregas'),
        '-',
        this.somarCampo('valor_bonificacao'),
        ''
      ]);

      return dados;
    },

    aplicarEstilosPorTipo(ws, dadosExportacao) {
      switch (this.tipo) {
        case 'vendedores':
          this.aplicarEstilosVendedores(ws);
          break;
        case 'convenios':
          this.aplicarEstilosConvenios(ws);
          break;
        case 'motoristas':
          this.aplicarEstilosMotoristas(ws, dadosExportacao);
          break;
      }
    },

    aplicarEstilosVendedores(ws) {
      const colunas = [
        { wch: 10 },  // Filial
        { wch: 30 },  // Vendedor
        { wch: 15 },  // Meta
        { wch: 15 },  // Venda Base
        { wch: 15 },  // Comissão Base
      ];

      this.bonificacoes.forEach(() => {
        colunas.push({ wch: 15 }); // Venda
        colunas.push({ wch: 15 }); // Comissão
      });

      colunas.push({ wch: 15 }); // Salário Total
      ws['!cols'] = colunas;

      // Aplicar formato de moeda
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let R = 1; R <= range.e.r; R++) {
        for (let C = 2; C <= range.e.c; C++) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) continue;
          ws[cellAddress].z = 'R$ #,##0.00';
        }
      }

      ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    },

    aplicarEstilosConvenios(ws) {
      const colunas = [
        { wch: 10 },  // Filial
        { wch: 30 },  // Vendedor
        { wch: 15 },  // Total Notas
        { wch: 15 },  // Comissão
      ];

      ws['!cols'] = colunas;

      // Aplicar formato de moeda apenas na coluna de comissão (índice 3)
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let R = 1; R <= range.e.r; R++) {
        // Coluna Total Notas (C=2) - formato de número
        const cellNotas = XLSX.utils.encode_cell({ r: R, c: 2 });
        if (ws[cellNotas]) {
          ws[cellNotas].z = '#,##0';
        }

        // Coluna Comissão (C=3) - formato de moeda
        const cellComissao = XLSX.utils.encode_cell({ r: R, c: 3 });
        if (ws[cellComissao]) {
          ws[cellComissao].z = 'R$ #,##0.00';
        }
      }

      ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    },

    aplicarEstilosMotoristas(ws) {
      const colunas = [
        { wch: 10 },  // Filial
        { wch: 30 },  // Motorista
        { wch: 15 },  // Meta Entregas
        { wch: 15 },  // Qtd Entregas
        { wch: 15 },  // Valor/Entrega
        { wch: 15 },  // Bonificação
        { wch: 15 },  // Status Meta
      ];

      ws['!cols'] = colunas;

      const range = XLSX.utils.decode_range(ws['!ref']);

      for (let R = 1; R <= range.e.r; R++) {
        // Coluna Meta Entregas (C=2) - número inteiro centralizado
        const cellMeta = XLSX.utils.encode_cell({ r: R, c: 2 });
        if (ws[cellMeta]) {
          ws[cellMeta].z = '#,##0';
          ws[cellMeta].s = { alignment: { horizontal: 'center' } };
        }

        // Coluna Qtd Entregas (C=3) - número inteiro centralizado
        const cellQtd = XLSX.utils.encode_cell({ r: R, c: 3 });
        if (ws[cellQtd]) {
          ws[cellQtd].z = '#,##0';
          ws[cellQtd].s = { alignment: { horizontal: 'center' } };
        }

        // Coluna Valor/Entrega (C=4) - moeda
        const cellValor = XLSX.utils.encode_cell({ r: R, c: 4 });
        if (ws[cellValor] && ws[cellValor].v !== '-') {
          ws[cellValor].z = 'R$ #,##0.00';
        }

        // Coluna Bonificação (C=5) - moeda
        const cellBonif = XLSX.utils.encode_cell({ r: R, c: 5 });
        if (ws[cellBonif]) {
          ws[cellBonif].z = 'R$ #,##0.00';
        }

        // Coluna Status Meta (C=6) - centralizado
        const cellStatus = XLSX.utils.encode_cell({ r: R, c: 6 });
        if (ws[cellStatus]) {
          ws[cellStatus].s = { alignment: { horizontal: 'center' } };

          // Adicionar cor de fundo baseado no status (apenas se não for cabeçalho ou total)
          if (R > 0 && R < range.e.r) {
            const status = ws[cellStatus].v;
            if (status === 'ATINGIU') {
              ws[cellStatus].s = {
                ...ws[cellStatus].s,
                fill: { fgColor: { rgb: "4CAF50" } },
                font: { color: { rgb: "FFFFFF" }, bold: true }
              };
            } else if (status === 'NÃO ATINGIU') {
              ws[cellStatus].s = {
                ...ws[cellStatus].s,
                fill: { fgColor: { rgb: "FF9800" } },
                font: { color: { rgb: "FFFFFF" }, bold: true }
              };
            }
          }
        }
      }

      ws['!freeze'] = { xSplit: 0, ySplit: 1 };
    },

    somarCampo(campo) {
      return this.dados
          .filter(v => v[campo] !== undefined)
          .map(v => v[campo])
          .reduce((a, v) => a + v, 0);
    },

    somarBonificacaoVenda(codigo) {
      return this.dados.reduce((total, vendedor) => {
        if (vendedor.bonificacoes && vendedor.bonificacoes[codigo]) {
          return total + (vendedor.bonificacoes[codigo].vendatotal || 0);
        }
        return total;
      }, 0);
    },

    somarBonificacaoComissao(codigo) {
      return this.dados.reduce((total, vendedor) => {
        if (vendedor.bonificacoes && vendedor.bonificacoes[codigo]) {
          return total + (vendedor.bonificacoes[codigo].comissao || 0);
        }
        return total;
      }, 0);
    }
  }
};
</script>

<style scoped>
/* Estilos opcionais */
</style>